import { Input } from './Input';
import { isMobilePlaySurface } from './mobileDetect';

type StickState = { id: number | null; x: number; y: number; active: boolean };

/**
 * On-screen left stick (move) + right look pad + action cluster for phones.
 * Feeds the shared Input object so gameplay code stays keyboard-shaped.
 */
export class MobileControls {
  readonly root: HTMLElement;
  private input: Input;
  private move: StickState = { id: null, x: 0, y: 0, active: false };
  private lookId: number | null = null;
  private lastLookX = 0;
  private lastLookY = 0;
  private visible = false;

  constructor(input: Input) {
    this.input = input;
    this.root = document.createElement('div');
    this.root.id = 'mobile-controls';
    this.root.innerHTML = `
      <div class="mc-stick" data-stick="move" aria-label="Move">
        <div class="mc-knob"></div>
      </div>
      <div class="mc-look" data-look aria-label="Look"></div>
      <div class="mc-actions">
        <button type="button" class="mc-btn" data-act="interact">Use</button>
        <button type="button" class="mc-btn mc-jump" data-act="jump">Jump</button>
        <button type="button" class="mc-btn" data-act="run">Run</button>
        <button type="button" class="mc-btn" data-act="crouch">Crouch</button>
      </div>
      <button type="button" class="mc-fs" data-act="fullscreen" title="Landscape fullscreen">⛶</button>
      <div class="mc-rotate" hidden>Rotate to landscape for the best keep view</div>
    `;
    document.getElementById('app')?.appendChild(this.root);
    this.bind();
    this.syncVisibility(false);
    window.addEventListener('resize', () => this.updateRotateHint());
    window.addEventListener('orientationchange', () => this.updateRotateHint());
  }

  syncVisibility(gameRunning: boolean): void {
    this.visible = gameRunning && isMobilePlaySurface();
    this.root.classList.toggle('visible', this.visible);
    this.root.setAttribute('aria-hidden', this.visible ? 'false' : 'true');
    // Hide desktop-only chrome so we never stack two control schemes.
    document.body.classList.toggle('mobile-play', this.visible);
    const hint = document.getElementById('controls-hint');
    const look = document.getElementById('look-hint');
    if (hint) hint.hidden = this.visible;
    if (look) look.hidden = this.visible;
    this.updateRotateHint();
  }

  private updateRotateHint(): void {
    const hint = this.root.querySelector('.mc-rotate') as HTMLElement;
    if (!hint) return;
    const portrait = window.innerHeight > window.innerWidth;
    hint.hidden = !(this.visible && portrait);
  }

  private bind(): void {
    const moveEl = this.root.querySelector('[data-stick="move"]') as HTMLElement;
    const lookEl = this.root.querySelector('[data-look]') as HTMLElement;
    const knob = moveEl.querySelector('.mc-knob') as HTMLElement;

    const setKnob = (nx: number, ny: number) => {
      knob.style.transform = `translate(calc(-50% + ${nx * 34}px), calc(-50% + ${ny * 34}px))`;
    };

    const onMove = (e: PointerEvent) => {
      if (this.move.id !== e.pointerId) return;
      const r = moveEl.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      let dx = (e.clientX - cx) / (r.width * 0.42);
      let dy = (e.clientY - cy) / (r.height * 0.42);
      const len = Math.hypot(dx, dy);
      if (len > 1) {
        dx /= len;
        dy /= len;
      }
      this.move.x = dx;
      this.move.y = dy;
      this.move.active = true;
      this.input.setTouchAxis(dx, dy);
      setKnob(dx, dy);
    };

    moveEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      moveEl.setPointerCapture(e.pointerId);
      this.move.id = e.pointerId;
      onMove(e);
    });
    moveEl.addEventListener('pointermove', onMove);
    const endMove = (e: PointerEvent) => {
      if (this.move.id !== e.pointerId) return;
      this.move = { id: null, x: 0, y: 0, active: false };
      this.input.setTouchAxis(0, 0);
      setKnob(0, 0);
    };
    moveEl.addEventListener('pointerup', endMove);
    moveEl.addEventListener('pointercancel', endMove);

    lookEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      lookEl.setPointerCapture(e.pointerId);
      this.lookId = e.pointerId;
      this.lastLookX = e.clientX;
      this.lastLookY = e.clientY;
      this.input.touchLooking = true;
    });
    lookEl.addEventListener('pointermove', (e) => {
      if (this.lookId !== e.pointerId) return;
      const dx = e.clientX - this.lastLookX;
      const dy = e.clientY - this.lastLookY;
      this.lastLookX = e.clientX;
      this.lastLookY = e.clientY;
      // Sensitivity tuned for soft third-person look
      this.input.addTouchLook(dx * 1.35, dy * 1.15);
    });
    const endLook = (e: PointerEvent) => {
      if (this.lookId !== e.pointerId) return;
      this.lookId = null;
      this.input.touchLooking = false;
    };
    lookEl.addEventListener('pointerup', endLook);
    lookEl.addEventListener('pointercancel', endLook);

    this.root.querySelectorAll('[data-act]').forEach((btn) => {
      const el = btn as HTMLElement;
      const act = el.dataset.act!;
      if (act === 'fullscreen') {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          void requestLandscapeFullscreen(document.documentElement);
        });
        return;
      }
      if (act === 'run' || act === 'crouch') {
        el.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          if (act === 'run') this.input.setTouchRun(true);
          else this.input.setTouchCrouch(true);
          el.classList.add('held');
        });
        const endHold = () => {
          if (act === 'run') this.input.setTouchRun(false);
          else this.input.setTouchCrouch(false);
          el.classList.remove('held');
        };
        el.addEventListener('pointerup', endHold);
        el.addEventListener('pointercancel', endHold);
        el.addEventListener('pointerleave', () => {
          if (el.classList.contains('held')) endHold();
        });
        return;
      }
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (act === 'jump') this.input.pulseTouchJump();
        if (act === 'interact') this.input.pulseTouchInteract();
        el.classList.add('held');
      });
      el.addEventListener('pointerup', () => el.classList.remove('held'));
      el.addEventListener('pointercancel', () => el.classList.remove('held'));
    });
  }
}

export async function requestLandscapeFullscreen(target: HTMLElement): Promise<void> {
  try {
    if (!document.fullscreenElement) {
      const req =
        target.requestFullscreen?.bind(target) ||
        (target as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen?.bind(
          target,
        );
      if (req) await Promise.resolve(req());
    }
  } catch {
    /* user gesture / iOS quirks */
  }
  try {
    const orient = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>;
    };
    if (orient?.lock) await orient.lock('landscape');
  } catch {
    /* Safari / Firefox often deny */
  }
}
